import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, Search, Send } from 'lucide-react';
import { medicalRecordsApi, memedApi } from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { CertificateEditor } from '@/components/medical-records/CertificateEditor';

interface Certificate {
  id: string;
  patientId: string;
  patientName: string;
  appointmentId: string;
  diagnosis: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  reasons: string;
  doctorObservations: string;
  status: 'DRAFT' | 'SIGNED' | 'ISSUED' | 'CANCELLED';
  createdAt: string;
  signedAt?: string;
  crm: string;
  memedId?: string;
  memedStatus?: 'PENDING' | 'SYNCED' | 'FAILED' | 'SIGNED' | 'CANCELLED';
  pdfUrl?: string | null;
  memedPdfUrl?: string | null;
}

interface CertificatesResponse {
  certificates: Certificate[];
  total: number;
  page: number;
  pageSize: number;
}

export function DoctorCertificatesPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const doctorId = user?.id || '';
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalData, setCreateModalData] = useState<{
    patientId: string;
    medicalRecordId: string;
    appointmentId?: string;
  } | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'ALL' | 'DRAFT' | 'SIGNED' | 'ISSUED'>('ALL');

  const sendToMemedMutation = useMutation({
    mutationFn: async (certificateId: string) => {
      await memedApi.sendCertificateToMemed(certificateId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctor', 'certificates'] });
      queryClient.invalidateQueries({ queryKey: ['memed', 'certificate'] });
    },
  });

  const downloadPdfMutation = useMutation({
    mutationFn: async (certificate: Certificate) => {
      const targetUrl = certificate.pdfUrl || certificate.memedPdfUrl;
      if (targetUrl) {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      try {
        const result = await medicalRecordsApi.downloadCertificatePdf(certificate.id);
        const data = (result as any)?.data || result;
        if (data?.url) {
          window.open(data.url, '_blank', 'noopener,noreferrer');
          return;
        }
        if (data?.pdf) {
          const mimeType = data.mimeType || 'application/pdf';
          const link = document.createElement('a');
          link.href = `data:${mimeType};base64,${data.pdf}`;
          link.download = `atestado_${certificate.id}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (err: any) {
        alert(err?.message || 'Não foi possível baixar o atestado oficial.');
      }
    },
  });

  const { data, isLoading, isError, refetch } = useQuery<CertificatesResponse>({
    queryKey: ['doctor', 'certificates', page, filter],
    queryFn: async () => {
      const response = await medicalRecordsApi.getDoctorCertificates(doctorId, page);
      return {
        certificates: response.data as any,
        total: response.meta.total,
        page: response.meta.page,
        pageSize: response.meta.limit,
      };
    },
  } as any);

  

  const filteredCertificates = data?.certificates.filter((c) => {
    const matchesSearch = c.patientName.toLowerCase().includes(search.toLowerCase()) ||
                          c.diagnosis.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'ALL' || c.status === filter;
    return matchesSearch && matchesFilter;
  }) || [];

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Não informada';
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(dateStr));
    } catch {
      return 'Data inválida';
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Atestados Médicos</h1>
          <p className="text-sm text-muted-foreground">Consulte o histórico de atestados emitidos e crie novos documentos</p>
        </div>
        <button
          onClick={() => navigate('/medico/atestados/novo')}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-semibold rounded-xl hover:bg-primary-hover shadow-xs transition-colors"
        >
          <Plus className="h-4 w-4" />
          Novo Atestado
        </button>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="relative flex-1 w-full max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por paciente ou diagnóstico..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          />
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="w-full sm:w-auto px-4 py-2 border border-border bg-background text-foreground rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-sm"
        >
          <option value="ALL">Todos os Status</option>
          <option value="DRAFT">Rascunho</option>
          <option value="SIGNED">Assinado</option>
          <option value="ISSUED">Emitido</option>
        </select>
      </div>

      {isLoading ? (
        <LoadingOverlay message="Carregando atestados..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar atestados" onRetry={() => refetch()} />
      ) : filteredCertificates.length === 0 ? (
        <EmptyState
          icon="FileText"
          title="Nenhum atestado encontrado"
          description={search || filter !== 'ALL' ? 'Tente ajustar os filtros' : 'Você ainda não criou nenhum atestado'}
          action={{ label: 'Criar Atestado', onClick: () => navigate('/medico/atestados/novo') }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCertificates.map((certificate) => {
            const memedStatus = certificate.memedStatus || (certificate.memedId ? 'SYNCED' : 'IDLE');
            const displayDate = certificate.createdAt || (certificate as any).issuedAt;
            return (
              <div
                key={certificate.id}
                className="rounded-2xl bg-card text-card-foreground border border-border shadow-sm p-5 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <span className="text-xs text-muted-foreground">
                      {formatDate(displayDate)}
                    </span>
                    <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                      memedStatus === 'SYNCED' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' :
                      memedStatus === 'PENDING' ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20' :
                      memedStatus === 'FAILED' ? 'bg-destructive/10 text-destructive border border-destructive/20' :
                      'bg-muted text-muted-foreground border border-border'
                    }`}>
                      {memedStatus === 'SYNCED' ? 'Enviado' :
                       memedStatus === 'PENDING' ? 'Pendente' :
                       memedStatus === 'FAILED' ? 'Erro' : 'Local'}
                    </span>
                  </div>

                  <h4 className="font-bold text-foreground text-base mb-1">
                    {certificate.patientName || 'Paciente'}
                  </h4>

                  <div className="mt-3 space-y-1.5 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">Motivo/CID:</span>
                      <span className="text-sm font-medium text-foreground truncate">
                        {certificate.diagnosis || 'Não especificado'}
                      </span>
                    </div>
                    {certificate.totalDays ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground uppercase tracking-wide">Afastamento:</span>
                        <span className="text-sm font-bold text-primary">{certificate.totalDays} dias</span>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-5 pt-4 border-t border-border flex flex-col gap-2">
                  <button
                    onClick={() => downloadPdfMutation.mutate(certificate)}
                    disabled={downloadPdfMutation.isPending}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-foreground bg-muted hover:bg-muted/80 rounded-xl transition-colors disabled:opacity-50"
                  >
                    <FileText className="h-4 w-4 text-primary" />
                    <span>Baixar Atestado Oficial (PDF)</span>
                  </button>

                  {memedStatus !== 'SYNCED' && (
                    <button
                      onClick={() => sendToMemedMutation.mutate(certificate.id)}
                      disabled={sendToMemedMutation.isPending}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-purple-600 dark:text-purple-400 bg-purple-500/10 hover:bg-purple-500/20 rounded-xl transition-colors disabled:opacity-50"
                    >
                      <Send className="h-4 w-4" />
                      <span>{sendToMemedMutation.isPending ? 'Sincronizando...' : 'Enviar para MeMed'}</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-center gap-2 pt-4">
        <button
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
          className="px-3 py-1.5 text-xs font-semibold border border-border bg-card text-foreground rounded-xl hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Anterior
        </button>
        <span className="px-3.5 py-1.5 text-xs font-bold bg-primary text-primary-foreground rounded-xl shadow-xs">{page}</span>
        <button
          onClick={() => setPage(page + 1)}
          disabled={page * 10 >= (data?.total || 0)}
          className="px-3 py-1.5 text-xs font-semibold border border-border bg-card text-foreground rounded-xl hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Próxima
        </button>
      </div>

      {showCreateModal && createModalData && (
        <CertificateEditor
          patientId={createModalData.patientId}
          appointmentId={createModalData.appointmentId}
          medicalRecordId={createModalData.medicalRecordId}
          doctorId={doctorId}
          open={showCreateModal}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['doctor', 'certificates'] });
            setShowCreateModal(false);
            setCreateModalData(null);
          }}
          onClose={() => {
            setShowCreateModal(false);
            setCreateModalData(null);
          }}
        />
      )}
    </div>
  );
}
